/**
 * L'identité visuelle d'un tableau : sa teinte et ses initiales.
 *
 * Les deux vont ensemble — elles forment l'écusson qui ouvre chaque ligne — et
 * se dérivent de la même source, le nom du tableau, pour qu'un tableau garde le
 * même visage d'un écran à l'autre.
 *
 * ── La teinte ──────────────────────────────────────────────────────────────
 *
 * Avec une centaine de tableaux, la couleur n'est pas un ornement : c'est le
 * moyen le plus rapide de reconnaître le sien dans une liste longue. Encore
 * faut-il qu'elle ne change jamais — une couleur tirée au hasard à chaque
 * chargement ne s'apprend pas.
 *
 * Celle que l'équipe a choisie prime toujours. À défaut, on en dérive une du
 * nom : le même nom rend toujours le même ton, et deux tableaux voisins dans
 * l'alphabet tombent rarement sur le même.
 *
 * La gamme est courte et tenue à une clarté proche : dix tons qui se
 * distinguent les uns des autres, assez sombres pour tenir sur un fond clair et
 * assez lumineux pour tenir sur un fond sombre. Aucun n'empiète sur les couleurs
 * d'état d'Element Plus — un rouge de retard ne doit jamais pouvoir se confondre
 * avec la teinte d'un tableau.
 */

const GAMME = [
    '#3B82F6', // bleu
    '#0EA5A5', // sarcelle
    '#8B5CF6', // violet
    '#E0447F', // framboise
    '#22A45D', // vert
    '#D98324', // ambre
    '#6366F1', // indigo
    '#A855C7', // orchidée
    '#65A30D', // olive
    '#E0575B', // corail
    '#0891B2', // cyan
    '#B45309', // bronze
]

/**
 * La couleur que la table pose d'office sur tout nouveau tableau.
 *
 * Personne ne l'a choisie : la retenir rendrait les cent tableaux du même bleu,
 * et la couleur cesserait d'identifier quoi que ce soit. On la traite donc comme
 * une absence de choix. Le jour où un écran permettra de choisir vraiment, une
 * équipe qui voudra ce bleu-là devra le reposer — c'est le bon compromis tant
 * que la colonne porte un défaut.
 */
const DEFAUT_DE_LA_TABLE = '#409eff'

/** Somme de contrôle simple : stable, bien répartie, et lisible à la relecture. */
function empreinte(texte) {
    let total = 0

    for (let i = 0; i < texte.length; i += 1) {
        total = (total * 31 + texte.charCodeAt(i)) % 100003
    }

    return total
}

export function teinteDe(nom, couleurChoisie = null) {
    if (couleurChoisie && String(couleurChoisie).toLowerCase() !== DEFAUT_DE_LA_TABLE) {
        return couleurChoisie
    }

    return GAMME[empreinte(String(nom || '')) % GAMME.length]
}

/**
 * La même teinte, très diluée : pour un fond de survol ou un aplat de section.
 * Calculée ici plutôt qu'en CSS pour rester lisible par tous les navigateurs du
 * parc, `color-mix` et `rgb(from …)` n'étant pas acquis partout.
 */
export function teinteDiluee(hex, opacite = 0.1) {
    const valeur = String(hex || '').replace('#', '')

    if (valeur.length !== 6) return 'transparent'

    const r = parseInt(valeur.slice(0, 2), 16)
    const v = parseInt(valeur.slice(2, 4), 16)
    const b = parseInt(valeur.slice(4, 6), 16)

    return `rgba(${r}, ${v}, ${b}, ${opacite})`
}

/**
 * Les initiales d'un tableau, telles qu'elles s'écrivent sur son écusson.
 *
 * On saute les mots-outils : « Migrer les VM » donne MV et non ML, « Mise en
 * place de LDAP » donne MP. Deux lettres suffisent à reconnaître un tableau
 * qu'on connaît, et trois commenceraient à se lire comme un mot.
 */
const MOTS_OUTILS = [
    'a', 'au', 'aux', 'dans', 'de', 'des', 'du', 'en', 'et', 'la', 'le', 'les',
    'par', 'pour', 'sur', 'un', 'une',
]

export function initialesDe(nom) {
    const mots = String(nom || '')
        .replace(/[^\p{L}\p{N}\s'-]/gu, ' ')
        .split(/[\s'-]+/)
        .filter(mot => mot && !MOTS_OUTILS.includes(mot.toLowerCase()))

    if (mots.length === 0) return '—'

    // Sans accent : « PÉ » se lit moins bien que « PE » à onze pixels, et deux
    // tableaux ne se distinguent jamais par la seule accentuation de l'initiale.
    return mots
        .slice(0, 2)
        .map(mot => mot[0].normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase())
        .join('')
}
